import React, { useMemo } from "react";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import { useTranslation } from "react-i18next";
import {
  deriveConversationContext,
  formatRelativeTime,
} from "../../utils/conversationContextUtils";
import { getMessageDate } from "./timelineUtils";

const ConversationContextBar = ({
  messages = [],
  compact = false,
  protocol = "",
  onProtocolClick,
  hideGroupChip = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const ctx = useMemo(() => deriveConversationContext(messages), [messages]);

  if (!messages?.length && !protocol) return null;

  const lastClientDate = getMessageDate(ctx.lastClientMessage);
  const lastClientChannel =
    ctx.lastClientMessage?.channelName || ctx.lastClientMessage?.channel || "";
  const protocolLabel = protocol != null && String(protocol).trim() !== ""
    ? String(protocol).trim()
    : "";

  if (compact) {
    const relative = lastClientDate ? formatRelativeTime(lastClientDate) : "";
    const groupLabel = !hideGroupChip && ctx.isGroup ? t("context.groupWhatsapp", "Grupo WhatsApp") : "";
    if (!protocolLabel && !relative && !groupLabel) return null;
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={[
          protocolLabel ? `${t("pane.protocol")}: ${protocolLabel}` : "",
          relative ? `${t("context.lastClientContact", "Último contato do cliente")}: ${relative}` : "",
          groupLabel,
        ]
          .filter(Boolean)
          .join(" · ")}
        sx={{
          display: "block",
          minWidth: 0,
          fontSize: "0.75rem",
          lineHeight: 1.35,
        }}
      >
        {protocolLabel ? (
          <Box
            component="span"
            onClick={onProtocolClick}
            sx={{
              fontWeight: 700,
              cursor: typeof onProtocolClick === "function" ? "pointer" : "default",
            }}
          >
            #{protocolLabel}
          </Box>
        ) : null}
        {protocolLabel && (relative || groupLabel) ? " · " : ""}
        {relative}
        {relative && groupLabel ? " · " : ""}
        {groupLabel}
      </Typography>
    );
  }

  if (!messages?.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        mt: 0.5,
      }}
    >
      {lastClientDate && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
          {t("context.lastClientContact", "Último contato do cliente")}:{" "}
          {formatRelativeTime(lastClientDate)}
          {lastClientChannel ? ` via ${lastClientChannel}` : ""}
        </Typography>
      )}
      {ctx.channelCount > 1 && (
        <Chip
          size="small"
          variant="outlined"
          label={t("context.channelsUsed", {
            count: ctx.channelCount,
            defaultValue: `${ctx.channelCount} canais nesta solicitação`,
          })}
          sx={{ height: 20, fontSize: "0.68rem" }}
        />
      )}
      {ctx.isGroup && !hideGroupChip && (
        <Chip
          icon={<GroupsIcon sx={{ fontSize: "14px !important" }} />}
          size="small"
          label={t("context.groupWhatsapp", "Grupo WhatsApp")}
          sx={{ height: 20, fontSize: "0.68rem", color: theme.palette.text.secondary }}
        />
      )}
    </Box>
  );
};

export default ConversationContextBar;
